'use client'

import Link from 'next/link'
import { useState } from 'react'
import { LuTrash2 } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import SafeImage from '@/features/veluvit/components/ui/media-image'
import { toast } from '@/core/lib/toast'

import PageHeader from '@/features/veluvit/components/shell/page-header'
import EmptyState from '@/features/veluvit/components/ui/empty-state'
import { openAuth } from '@/features/veluvit/services/account.actions'
import {
  deleteNovel,
  NOVEL_LENGTHS,
} from '@/features/veluvit/services/novel.service'
import { useAppReadScope } from '@/features/veluvit/hooks/use-app-read-scope'
import { appSwrKey } from '@/features/veluvit/lib/app-read'
import { useCachedSWR } from '@/core/lib/swr/use-cached-swr'
import { novelListSchema } from '@/features/veluvit/schemas/novel.schema'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { Route } from 'next'

import type {
  NovelSummary,
  NovelList,
} from '@/features/veluvit/schemas/novel.schema'
import type { NovelListInitial } from '@/features/veluvit/services/novel-read.server'

/** 서재 한 권 — 표지 + 제목. 지우기는 두 번 눌러야 지워진다(실수 방지) */
function NovelTile({
  novel,
  onDelete,
}: {
  novel: NovelSummary
  onDelete: () => void
}) {
  const [ask, setAsk] = useState(false)
  return (
    <figure className="m-0 flex min-w-0 flex-col">
      <Link
        href={`/novel/${novel.id}` as Route}
        className="group relative block aspect-[3/4] overflow-hidden rounded-panel bg-surface no-underline"
      >
        {novel.cover && (
          <SafeImage
            src={novel.cover}
            alt=""
            fill
            sizes="(min-width: 1001px) 25vw, 50vw"
            className="object-cover transition-transform duration-500 ease-smooth group-hover:scale-[1.03]"
            appearance="plain"
          />
        )}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[linear-gradient(to_top,rgb(0_0_0/0.88),rgb(0_0_0/0.1)_60%)]"
        />
        <div className="absolute inset-x-0 bottom-0 p-3.5">
          <b className="line-clamp-3 text-c18 leading-[1.3] font-bold text-t1 title-glow">
            {novel.title}
          </b>
        </div>
      </Link>
      <figcaption className="mt-2.5 flex min-w-0 items-center gap-1">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-c13 text-t2">
            {novel.charName}
          </span>
          <span className="block text-c12 text-t3 tabular-nums">
            {NOVEL_LENGTHS[novel.length].label} ·{' '}
            {new Date(novel.createdAt).toLocaleDateString('ko-KR', {
              timeZone: 'Asia/Seoul',
            })}
          </span>
        </span>
        {ask ? (
          <Button
            variant="danger"
            size="sm"
            shape="pill"
            onClick={onDelete}
            onBlur={() => setAsk(false)}
          >
            지우기
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon-sm"
            shape="pill"
            aria-label="소설 지우기"
            title="지우기"
            onClick={() => setAsk(true)}
          >
            <LuTrash2 aria-hidden="true" className="size-4 text-t3" />
          </Button>
        )}
      </figcaption>
    </figure>
  )
}

/** 내 서재 — 대화에서 만든 엔딩 소설 모음 (flow-renew ⑦) */
export default function NovelsScreen({
  initial,
}: {
  initial: NovelListInitial
}) {
  const scope = useAppReadScope()
  const sameScope = scope === initial.epoch
  const identity = useSessionStore((state) => state.identity)
  const key =
    sameScope && initial.auth === 'member'
      ? appSwrKey(initial.epoch, '/novels/')
      : null
  const {
    data: response,
    error: readError,
    mutate,
  } = useCachedSWR<NovelList>(key, 'REALTIME', {
    responseSchema: novelListSchema,
    revalidateOnMount: false,
    revalidateOnFocus: false,
  })
  const [busy, setBusy] = useState(false)
  const result = sameScope ? (response ?? initial.response) : undefined
  const items = result?.ok ? result.data.novels : null
  const loginRequired =
    sameScope &&
    (initial.auth === 'guest' ||
      (!result?.ok && (result?.status === 401 || result?.status === 403)))
  const error = !sameScope
    ? ''
    : loginRequired
      ? '서재를 보려면 로그인해 주세요.'
      : (readError?.message ??
        (initial.auth === 'unavailable'
          ? '서재를 불러오지 못했어요.'
          : result && !result.ok
            ? result.message
            : ''))

  const remove = async (id: string) => {
    if (!sameScope || initial.auth !== 'member' || busy) return
    setBusy(true)
    try {
      await deleteNovel(id)
      if (
        useSessionStore.getState().identity !== identity ||
        useSessionStore.getState().data?.user?.id !== initial.owner
      )
        return
      await mutate(
        (previous) => {
          const current = previous ?? response ?? initial.response
          return current?.ok
            ? {
                ...current,
                data: {
                  novels: current.data.novels.filter((item) => item.id !== id),
                },
              }
            : current
        },
        { revalidate: false }
      )
      toast.show('소설을 지웠어요', { duration: 1400 })
    } catch (cause) {
      if (useSessionStore.getState().identity === identity)
        toast.show(cause instanceof Error ? cause.message : '지우지 못했어요')
    } finally {
      setBusy(false)
    }
  }

  const retry = () => {
    if (key) void mutate().catch(() => {})
  }

  return (
    <>
      <PageHeader back title="내 서재" />
      <section className="mx-auto w-full max-w-[1100px] px-4 pt-5 pb-10 over6:px-5 over6:pt-6">
        <p className="text-c13 leading-[1.6] text-t3 over10:text-c14">
          대화의 결말을 한 편의 소설로 만든 엔딩 소설이에요. 눌러서 다시 읽을 수
          있어요.
        </p>
        {error ? (
          <div className="mt-6">
            <p role="alert" className="text-c13 text-error">
              {loginRequired ? '서재를 보려면 로그인해 주세요.' : error}
            </p>
            {loginRequired ? (
              <Button
                variant="primary"
                size="md"
                shape="pill"
                className="mt-4"
                onClick={() => openAuth('login')}
              >
                로그인
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="md"
                shape="pill"
                className="mt-4"
                onClick={retry}
              >
                다시 불러오기
              </Button>
            )}
          </div>
        ) : items === null ? (
          <p className="mt-6 text-c13 text-t3">불러오고 있어요…</p>
        ) : items.length === 0 ? (
          <EmptyState
            title="아직 서재에 소설이 없어요"
            description="소설을 만들 수 있는 작품에서는 대화방 메뉴에서 시작할 수 있어요."
            icon={
              <SafeImage
                src="/images/veluvit/empty-library.png"
                width={160}
                height={160}
                sizes="160px"
                alt=""
                appearance="plain"
                className="size-40 object-contain"
              />
            }
            action={
              <Link
                href="/chats"
                className="text-point underline underline-offset-4 hover:text-point-soft focus-visible:text-point-soft"
              >
                내 대화 보기
              </Link>
            }
          />
        ) : (
          <div className="mt-5 grid grid-cols-2 gap-x-3 gap-y-6 over6:grid-cols-3 over10:grid-cols-4 over10:gap-x-5">
            {items.map((x) => (
              <NovelTile
                key={x.id}
                novel={x}
                onDelete={() => void remove(x.id)}
              />
            ))}
          </div>
        )}
      </section>
    </>
  )
}
